/**
 * OPCI EDC Main Application
 */

import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';

// Pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import SubjectList from './pages/SubjectList';
import DataEntry from './pages/DataEntry';
import SiteManagement from './pages/SiteManagement';
import UserManagement from './pages/UserManagement';
import CecDashboard from './pages/CecDashboard';
import QueryManagement from './pages/QueryManagement';
import AuditTrail from './pages/AuditTrail';
import ChangePassword from './pages/ChangePassword';
import Profile from './pages/Profile';

import { queryClient } from './queryClient';

// Placeholder components for pages not yet built
const Placeholder: React.FC<{ title: string }> = ({ title }) => (
  <div className="bg-white rounded-xl shadow-sm p-12 text-center">
    <h1 className="text-2xl font-bold text-gray-800 mb-4">{title}</h1>
    <p className="text-gray-600">This page is under development</p>
  </div>
);

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<Login />} />

            {/* Force Password Change */}
            <Route
              path="/change-password"
              element={
                <ProtectedRoute>
                  <ChangePassword />
                </ProtectedRoute>
              }
            />

            {/* Protected Routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Dashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/subjects"
              element={
                <ProtectedRoute>
                  <Layout>
                    <SubjectList />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/subjects/:subjectId"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Placeholder title="Subject Detail" />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/data"
              element={
                <ProtectedRoute>
                  <Layout>
                    <DataEntry />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/queries"
              element={
                <ProtectedRoute>
                  <Layout>
                    <QueryManagement />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/audit"
              element={
                <ProtectedRoute>
                  <Layout>
                    <AuditTrail />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* CEC Routes */}
            <Route
              path="/cec"
              element={
                <ProtectedRoute requiredRole="cec">
                  <Layout>
                    <CecDashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* CEC adjudication links go to /data?subject=...&section=cec */}

            {/* Admin Routes */}
            <Route
              path="/admin/users"
              element={
                <ProtectedRoute requiredRole="site-rc">
                  <Layout>
                    <UserManagement />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/sites"
              element={
                <ProtectedRoute requiredRole="admin">
                  <Layout>
                    <SiteManagement />
                  </Layout>
                </ProtectedRoute>
              }
            />


            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Profile />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Default redirect */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
